import { Snowflake, Volume2, Layers, Weight, Ruler, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const SPEC_ROWS = [{
  Icon: Ruler,
  label: "Total panel thickness",
  value: "42 mm"
}, {
  Icon: Snowflake,
  label: "Thermal rating",
  value: "R-2.0"
}, {
  Icon: Volume2,
  label: "Acoustic rating (STC)",
  value: "27+"
}, {
  Icon: Layers,
  label: "Polyurethane core",
  value: "CFC-free"
}, {
  Icon: Weight,
  label: "Weight per m²",
  value: "~6 kg"
}];
export default function InsulatedAnatomy() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 right-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(90deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 left-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(270deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              04 · Anatomy of a Panel
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>
            Five layers.
            <br />
            <span style={{
            color: GOLD_DARK
          }}>
              Each one earning its place.
            </span>
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: INK_SOFT,
          maxWidth: "640px"
        }}>
            Inside every insulated panel is a layered sandwich engineered for
            Sydney conditions — UV-stable paint, structural steel, foamed core,
            and edge-sealed thermal breaks. Here's the cross-section.
          </p>
        </div>

        {/* Diagram + spec sheet grid */}
        <div className="relative rounded-2xl overflow-hidden" style={{
        backgroundColor: "#ffffff",
        boxShadow: "0 30px 80px rgba(8,20,56,0.15), 0 0 0 1px rgba(8,20,56,0.06)"
      }}>
          {/* Top gold edge */}
          <div aria-hidden className="absolute top-0 left-0 right-0 h-1.5" style={{
          background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
        }} />

          {/* Letterhead */}
          <div className="relative px-7 md:px-10 py-6 flex flex-wrap items-center justify-between gap-3 border-b-2" style={{
          borderColor: NAVY
        }}>
            <div>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD_DARK,
              marginBottom: "4px"
            }}>
                Gold Garage Door · Technical Cross-Section
              </div>
              <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.2rem",
              color: NAVY,
              letterSpacing: "-0.02em",
              lineHeight: 1.15
            }}>
                Insulated Sectional Panel · 42 mm
              </h3>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{
            backgroundColor: `${GOLD}15`,
            border: `1px solid ${GOLD}44`
          }}>
              <div style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: GOLD
            }} />
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                Live spec
              </span>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0">
            {/* LEFT — SVG diagram */}
            <div className="relative p-8 md:p-12 lg:border-r" style={{
            borderColor: "rgba(8,20,56,0.1)"
          }}>
              {/* Grid background */}
              <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: `linear-gradient(${INK_SOFT}11 1px, transparent 1px), linear-gradient(90deg, ${INK_SOFT}11 1px, transparent 1px)`,
              backgroundSize: "24px 24px",
              opacity: 0.6
            }} />

              <div className="relative" style={{
              maxWidth: 560,
              margin: "0 auto"
            }}>
                <svg viewBox="0 0 600 520" xmlns="http://www.w3.org/2000/svg" style={{
                width: "100%",
                height: "auto",
                display: "block"
              }} aria-label="Cross-section diagram of an insulated garage door panel">
                  {/* === EXTERIOR LABEL === */}
                  <text x="300" y="32" fill={NAVY} fontFamily={FONT_HEAD} fontSize="14" fontWeight="800" textAnchor="middle" letterSpacing="3">
                    EXTERIOR
                  </text>
                  <text x="300" y="50" fill={INK_SOFT} fontFamily={FONT_BODY} fontSize="11" textAnchor="middle">
                    Street-facing side
                  </text>

                  {/* Sun + heat arrows from top */}
                  <g opacity="0.7">
                    <circle cx="120" cy="80" r="14" fill={GOLD} />
                    <g stroke={GOLD} strokeWidth="2" strokeLinecap="round">
                      <line x1="120" y1="60" x2="120" y2="50" />
                      <line x1="120" y1="100" x2="120" y2="110" />
                      <line x1="100" y1="80" x2="90" y2="80" />
                      <line x1="140" y1="80" x2="150" y2="80" />
                      <line x1="106" y1="66" x2="99" y2="59" />
                      <line x1="134" y1="94" x2="141" y2="101" />
                      <line x1="106" y1="94" x2="99" y2="101" />
                      <line x1="134" y1="66" x2="141" y2="59" />
                    </g>
                  </g>
                  <g stroke={GOLD_DARK} strokeWidth="1.5" fill="none" strokeLinecap="round">
                    <path d="M 200 70 L 200 130" markerEnd="url(#heatArrow)" />
                    <path d="M 240 70 L 240 130" markerEnd="url(#heatArrow)" />
                    <path d="M 280 70 L 280 130" markerEnd="url(#heatArrow)" />
                    <path d="M 320 70 L 320 130" markerEnd="url(#heatArrow)" />
                    <path d="M 360 70 L 360 130" markerEnd="url(#heatArrow)" />
                    <path d="M 400 70 L 400 130" markerEnd="url(#heatArrow)" />
                  </g>
                  <text x="490" y="100" fill={GOLD_DARK} fontFamily={FONT_HEAD} fontSize="10" fontWeight="700" letterSpacing="1.5">
                    HEAT IN
                  </text>

                  <defs>
                    <marker id="heatArrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                      <path d="M 0 0 L 8 4 L 0 8 z" fill={GOLD_DARK} />
                    </marker>
                    <marker id="coolArrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
                      <path d="M 0 0 L 8 4 L 0 8 z" fill="#4A90C2" />
                    </marker>
                    <pattern id="foamPattern" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
                      <circle cx="3" cy="3" r="2" fill={GOLD_DARK} opacity="0.25" />
                      <circle cx="10" cy="8" r="2.5" fill={GOLD_DARK} opacity="0.2" />
                      <circle cx="6" cy="11" r="1.8" fill={GOLD_DARK} opacity="0.3" />
                      <circle cx="12" cy="2" r="1.5" fill={GOLD_DARK} opacity="0.25" />
                    </pattern>
                  </defs>

                  {/* === PANEL CROSS-SECTION === */}
                  {/* Panel outline */}
                  <g>
                    {/* Layer 1: Exterior pre-painted steel */}
                    <rect x="150" y="150" width="300" height="14" fill={NAVY} stroke={NAVY} strokeWidth="1" />
                    <rect x="150" y="150" width="300" height="3" fill={GOLD} />

                    {/* Layer 2: Polyurethane foam core */}
                    <rect x="150" y="164" width="300" height="160" fill={CREAM_DEEP} stroke={NAVY} strokeWidth="1" />
                    <rect x="150" y="164" width="300" height="160" fill="url(#foamPattern)" />

                    {/* Layer 3: Interior pre-painted steel */}
                    <rect x="150" y="324" width="300" height="14" fill={NAVY} stroke={NAVY} strokeWidth="1" />
                    <rect x="150" y="335" width="300" height="3" fill="#ffffff" />

                    {/* Edge thermal break (left + right) */}
                    <rect x="146" y="150" width="4" height="188" fill={GOLD_DARK} opacity="0.4" />
                    <rect x="450" y="150" width="4" height="188" fill={GOLD_DARK} opacity="0.4" />
                  </g>

                  {/* === ANNOTATIONS — Leader lines + labels === */}

                  {/* Annotation 1: Exterior skin */}
                  <g>
                    <line x1="450" y1="157" x2="510" y2="157" stroke={NAVY} strokeWidth="1" />
                    <circle cx="450" cy="157" r="3" fill={GOLD} stroke={NAVY} strokeWidth="1.5" />
                    <text x="518" y="153" fill={NAVY} fontFamily={FONT_HEAD} fontSize="11" fontWeight="800">
                      01 · Steel skin
                    </text>
                    <text x="518" y="167" fill={INK_SOFT} fontFamily={FONT_BODY} fontSize="10">
                      0.4 mm · ColorBond
                    </text>
                  </g>

                  {/* Annotation 2: Foam core */}
                  <g>
                    <line x1="450" y1="244" x2="510" y2="244" stroke={NAVY} strokeWidth="1" />
                    <circle cx="450" cy="244" r="3" fill={GOLD} stroke={NAVY} strokeWidth="1.5" />
                    <text x="518" y="240" fill={NAVY} fontFamily={FONT_HEAD} fontSize="11" fontWeight="800">
                      02 · PU foam core
                    </text>
                    <text x="518" y="254" fill={INK_SOFT} fontFamily={FONT_BODY} fontSize="10">
                      40 mm · R-2.0 · CFC-free
                    </text>
                  </g>

                  {/* Annotation 3: Interior skin */}
                  <g>
                    <line x1="450" y1="331" x2="510" y2="331" stroke={NAVY} strokeWidth="1" />
                    <circle cx="450" cy="331" r="3" fill={GOLD} stroke={NAVY} strokeWidth="1.5" />
                    <text x="518" y="327" fill={NAVY} fontFamily={FONT_HEAD} fontSize="11" fontWeight="800">
                      03 · Interior skin
                    </text>
                    <text x="518" y="341" fill={INK_SOFT} fontFamily={FONT_BODY} fontSize="10">
                      0.4 mm · matt white
                    </text>
                  </g>

                  {/* Annotation 4: Edge thermal break (on the left) */}
                  <g>
                    <line x1="148" y1="244" x2="90" y2="244" stroke={NAVY} strokeWidth="1" />
                    <circle cx="148" cy="244" r="3" fill={GOLD} stroke={NAVY} strokeWidth="1.5" />
                    <text x="86" y="240" fill={NAVY} fontFamily={FONT_HEAD} fontSize="11" fontWeight="800" textAnchor="end">
                      04 · Edge break
                    </text>
                    <text x="86" y="254" fill={INK_SOFT} fontFamily={FONT_BODY} fontSize="10" textAnchor="end">
                      Anti-condensation
                    </text>
                  </g>

                  {/* Annotation 5: Weather seal (drawn under panel) */}
                  <rect x="150" y="342" width="300" height="10" fill={GOLD_DARK} opacity="0.55" rx="2" />
                  <g>
                    <line x1="148" y1="347" x2="90" y2="347" stroke={NAVY} strokeWidth="1" />
                    <circle cx="148" cy="347" r="3" fill={GOLD} stroke={NAVY} strokeWidth="1.5" />
                    <text x="86" y="343" fill={NAVY} fontFamily={FONT_HEAD} fontSize="11" fontWeight="800" textAnchor="end">
                      05 · Weather seal
                    </text>
                    <text x="86" y="357" fill={INK_SOFT} fontFamily={FONT_BODY} fontSize="10" textAnchor="end">
                      EPDM, UV-stable
                    </text>
                  </g>

                  {/* Total thickness dimension */}
                  <g>
                    <line x1="140" y1="150" x2="140" y2="338" stroke={GOLD_DARK} strokeWidth="1" />
                    <line x1="135" y1="150" x2="145" y2="150" stroke={GOLD_DARK} strokeWidth="1" />
                    <line x1="135" y1="338" x2="145" y2="338" stroke={GOLD_DARK} strokeWidth="1" />
                    <text x="124" y="248" fill={GOLD_DARK} fontFamily={FONT_HEAD} fontSize="11" fontWeight="800" textAnchor="end" transform="rotate(-90, 124, 248)">
                      42 mm total
                    </text>
                  </g>

                  {/* === BOTTOM SIDE — INTERIOR === */}
                  {/* Cool/quiet arrows from below */}
                  <g stroke="#4A90C2" strokeWidth="1.5" fill="none" strokeLinecap="round">
                    <path d="M 200 430 L 200 380" markerEnd="url(#coolArrow)" />
                    <path d="M 240 430 L 240 380" markerEnd="url(#coolArrow)" />
                    <path d="M 280 430 L 280 380" markerEnd="url(#coolArrow)" />
                    <path d="M 320 430 L 320 380" markerEnd="url(#coolArrow)" />
                    <path d="M 360 430 L 360 380" markerEnd="url(#coolArrow)" />
                    <path d="M 400 430 L 400 380" markerEnd="url(#coolArrow)" />
                  </g>
                  <text x="490" y="412" fill="#4A90C2" fontFamily={FONT_HEAD} fontSize="10" fontWeight="700" letterSpacing="1.5">
                    COOL IN
                  </text>

                  <text x="300" y="470" fill={NAVY} fontFamily={FONT_HEAD} fontSize="14" fontWeight="800" textAnchor="middle" letterSpacing="3">
                    INTERIOR
                  </text>
                  <text x="300" y="488" fill={INK_SOFT} fontFamily={FONT_BODY} fontSize="11" textAnchor="middle">
                    Your garage / room above
                  </text>
                </svg>
              </div>
            </div>

            {/* RIGHT — Spec sheet */}
            <div className="p-8 md:p-10 relative">
              <div className="mb-6">
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: GOLD_DARK,
                marginBottom: "8px"
              }}>
                  Spec Sheet
                </div>
                <h3 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "1.5rem",
                lineHeight: 1.15,
                color: NAVY,
                letterSpacing: "-0.02em"
              }}>
                  The numbers that matter.
                </h3>
              </div>

              <ul className="space-y-4 mb-7">
                {SPEC_ROWS.map(({
                Icon,
                label,
                value
              }) => <li key={label} className="flex items-center justify-between gap-4 pb-4 border-b" style={{
                borderColor: "rgba(8,20,56,0.1)"
              }}>
                    <div className="flex items-center gap-3">
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md" style={{
                    width: 32,
                    height: 32,
                    backgroundColor: `${GOLD}15`,
                    border: `1px solid ${GOLD}33`
                  }}>
                        <Icon size={15} color={GOLD_DARK} strokeWidth={2.5} />
                      </span>
                      <span style={{
                    fontFamily: FONT_BODY,
                    fontSize: "13.5px",
                    color: INK,
                    fontWeight: 500,
                    lineHeight: 1.35
                  }}>
                        {label}
                      </span>
                    </div>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "16px",
                  fontWeight: 800,
                  color: NAVY,
                  letterSpacing: "-0.01em"
                }}>
                      {value}
                    </span>
                  </li>)}
              </ul>

              {/* Note */}
              <div className="p-4 rounded-lg mb-6" style={{
              backgroundColor: CREAM_DEEP,
              border: `1px dashed ${GOLD}55`
            }}>
                <p style={{
                fontFamily: FONT_BODY,
                fontSize: "12.5px",
                lineHeight: 1.55,
                color: INK_SOFT
              }}>
                  <span style={{
                  fontFamily: FONT_HEAD,
                  fontWeight: 700,
                  color: NAVY
                }}>
                    Note:
                  </span>{" "}
                  Specs shown are typical for 42&nbsp;mm sectional panels.
                  Roller doors, premium ColorBond ranges, and commercial
                  panels may vary — we'll confirm exact specs at quote.
                </p>
              </div>

              <a href="tel:0485000933" className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full transition-all hover:scale-[1.01]" style={{
              backgroundColor: NAVY,
              color: "#ffffff",
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "14px",
              boxShadow: `0 8px 24px ${NAVY}33`
            }}>
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Phone size={15} strokeWidth={2.5} />
                </span>
                Get a spec for your home
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <ArrowRight size={14} strokeWidth={2.5} />
                </span>
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="relative px-7 md:px-10 py-5 flex flex-wrap items-center justify-between gap-3" style={{
          backgroundColor: CREAM_DEEP,
          borderTop: `2px solid ${NAVY}`
        }}>
            <span style={{
            fontFamily: FONT_BODY,
            fontSize: "12px",
            color: INK_SOFT,
            fontStyle: "italic"
          }}>
              Cross-section diagram · not to scale · for illustration only.
            </span>
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "11px",
            fontWeight: 800,
            letterSpacing: "0.22em",
            color: GOLD_DARK
          }}>
              GG · EST 2009
            </span>
          </div>
        </div>
      </div>
    </section>;
}